import React from 'react';
import styled, { keyframes, css } from 'styled-components';
import { CHECKBOX_TICK_SVG } from '@constants';
import Colors from './Colors';
import Spinner from './Spinner';

interface Props {
  checked: boolean;
  onClick?: () => void;
  color?: string;
  style?: any;
  loading?: boolean;
  dataCy?: string;
  design?: string;
}

export default function Checkbox({ checked, onClick, color = Colors.navyBlue, style, loading, design = 'standard' }: Props) {
  function renderTick() {
    if (loading) {
      return <Spinner />;
    }

    return <TickImage src={CHECKBOX_TICK_SVG} alt="tick" $checked={checked} />;
  }

  return (
    <StyledCheckbox onClick={onClick} style={style} $design={design} $color={color} $checked={checked}>
      {renderTick()}
    </StyledCheckbox>
  );
}

const scaleUp = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

const fadeOut = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; }
`;

const TickImage = styled.img<{ $checked: boolean }>`
  margin-top: 1px;

  ${(props) =>
    props.$checked
      ? css`
          animation: ${scaleUp} 0.2s ease-in-out;
          opacity: 1;
        `
      : css`
          animation: ${fadeOut} 0.2s forwards;
        `}
`;

const getCheckboxStyles = (design: string) => {
  switch (design) {
    case 'modern':
      return css`
        border-radius: 5px;
        background: ${Colors.paleGold50};
        width: 18px;
        height: 18px;
        border-width: 0;
      `;
    case 'compact':
      return css`
        border-radius: 5px;
        background: transparent;
        width: 20px;
        height: 20px;
        border-width: 2.5px;

        img {
          margin-top: 2px;
        }
      `;
    default:
      return css`
        border-radius: 2px;
        background: transparent;
        width: 25px;
        height: 25px;
        border-width: 2px;
      `;
  }
};

const StyledCheckbox = styled.div<{ $design: string; $color: string; $checked: boolean }>`
  display: flex;
  flex-shrink: 0;
  border: 2px solid ${(props) => props.$color};
  border-radius: ${(props) => (props.$design === 'modern' ? '5px' : '2px')};
  background: ${(props) => (props.$design === 'modern' ? Colors.paleGold50 : 'transparent')};
  width: ${(props) => (props.$design === 'modern' ? '18px' : '25px')};
  height: ${(props) => (props.$design === 'modern' ? '18px' : '25px')};
  justify-content: center;
  align-items: center;
  border-width: ${(props) => (props.$design === 'modern' ? '0' : '2px')};
  transition: background 0.2s, border-color 0.2s, transform 0.2s;

  ${(props) => getCheckboxStyles(props.$design)}

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
  }
`;
